import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toLocalYMD } from '../lib/dates';
import { 
  ArrowLeft, MapPin, Navigation, Check, Clock, ChevronRight, ChevronLeft,
  Phone, Mail, User, Filter, X, Calendar, Loader2,
  CheckCircle2, XCircle, SkipForward, PoundSterling, CreditCard,
  Send, RefreshCw, Maximize2, Minimize2, ArrowUpDown, GripVertical,
  ArrowUp, ArrowDown, RotateCcw, StickyNote
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import PaymentFailureModal from '../components/modals/PaymentFailureModal';
import PinCorrectionPromptModal from '../components/PinCorrectionPromptModal';
import JobDetailPanel from '../components/JobDetailPanel';
import InvoiceSummaryModal from '../components/InvoiceSummaryModal';
import NavigationModal from '../components/NavigationModal';
import { zoneColors } from '../data/csvData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get zone color
const getZoneColor = (zone) => {
  return zoneColors[zone] || zoneColors['default'];
};

// ============================================
// MODERN TILE JOB CARD - Route Sheet
// ============================================
const SwipeableJobCard = ({ 
  job, 
  index, 
  onClick, 
  isSelected, 
  onComplete, 
  onDoneAndPaid,
  onSkip, 
  onRestore,
  onUndoComplete,
  getStaffColor,
  onNavigate,
  hidePrice = false,
  isWorker = false,
  workerPermissions = {}
}) => {
  const [swipeX, setSwipeX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [didSwipe, setDidSwipe] = useState(false);
  
  const SWIPE_THRESHOLD = 80;
  
  const status = job.status || 'scheduled';
  const isCompleted = status === 'done' || status === 'completed';
  const isSkipped = status === 'skipped';
  
  const hasPaymentMethod = useMemo(() => {
    const pm = job.customer_payment_method || job.payment_method;
    return pm && pm !== 'none' && pm !== '';
  }, [job.customer_payment_method, job.payment_method]);
  
  const paymentType = job.customer_payment_method || job.payment_method || 'none';
  
  // Get short frequency
  const getFrequencyShort = () => {
    const freq = job.frequency || job.customer_frequency;
    if (!freq || freq === 'one-off') return null;
    const match = freq.match(/(\d+)\s*week/i);
    if (match) return `${match[1]}w`;
    return null;
  };
  
  // Touch handlers
  const handleStart = (clientX) => {
    setIsDragging(true);
    setStartX(clientX);
    setDidSwipe(false);
  };
  
  const handleMove = (clientX) => {
    if (!isDragging) return;
    const diff = clientX - startX;
    const limitedDiff = Math.max(-150, Math.min(150, diff));
    setSwipeX(limitedDiff);
    if (Math.abs(diff) > 10) setDidSwipe(true);
  };
  
  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (isCompleted && Math.abs(swipeX) > SWIPE_THRESHOLD) {
      onUndoComplete && onUndoComplete(job);
    } else if (swipeX > SWIPE_THRESHOLD) {
      if (isSkipped) {
        onRestore && onRestore(job);
      } else {
        setShowCompleteModal(true);
      }
    } else if (swipeX < -SWIPE_THRESHOLD && !isSkipped) {
      setShowSkipModal(true);
    }
    setSwipeX(0);
  };
  
  const handleClick = (e) => {
    if (didSwipe) { e.stopPropagation(); return; }
    onClick && onClick();
  };
  
  const handleComplete = (sendNotification = true) => {
    setShowCompleteModal(false);
    onComplete(job, { sendNotification, hasPaymentMethod, paymentType });
  };
  
  const handleSkip = (reason, newDate = null) => {
    setShowSkipModal(false);
    onSkip(job, reason, newDate);
  };
  
  const rightBgOpacity = Math.min(swipeX / SWIPE_THRESHOLD, 1);
  const leftBgOpacity = Math.min(-swipeX / SWIPE_THRESHOLD, 1);
  const iconScale = Math.min(Math.abs(swipeX) / SWIPE_THRESHOLD, 1);
  
  // Get address parts
  const addressParts = (job.job_address || job.address_override || job.customer_address || job.address || 'No address').split(',').map(p => p.trim());
  const streetAddress = addressParts.slice(0, 2).join(', ');

  return (
    <>
      <div className="relative mb-2 mx-2">
        {/* Background swipe actions */}
        <div className="absolute inset-0 rounded-xl overflow-hidden flex">
          {isCompleted ? (
            <>
              <div className="flex-1 bg-slate-600 flex items-center justify-end px-6" style={{ opacity: leftBgOpacity }}>
                <div className="text-white text-center" style={{ transform: `scale(${0.7 + iconScale * 0.3})` }}>
                  <RefreshCw size={20} className="mx-auto mb-1" />
                  <span className="text-xs font-semibold">Undo</span>
                </div>
              </div>
              <div className="flex-1 bg-slate-600 flex items-center justify-start px-6" style={{ opacity: rightBgOpacity }}>
                <div className="text-white text-center" style={{ transform: `scale(${0.7 + iconScale * 0.3})` }}>
                  <RefreshCw size={20} className="mx-auto mb-1" />
                  <span className="text-xs font-semibold">Undo</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex-1 bg-amber-500 flex items-center justify-end px-6" style={{ opacity: leftBgOpacity }}>
                <div className="text-white text-center" style={{ transform: `scale(${0.7 + iconScale * 0.3})` }}>
                  <SkipForward size={20} className="mx-auto mb-1" />
                  <span className="text-xs font-semibold">Skip</span>
                </div>
              </div>
              <div className={`flex-1 ${isSkipped ? 'bg-blue-500' : 'bg-emerald-500'} flex items-center justify-start px-6`} style={{ opacity: rightBgOpacity }}>
                <div className="text-white text-center" style={{ transform: `scale(${0.7 + iconScale * 0.3})` }}>
                  {isSkipped ? <RefreshCw size={20} className="mx-auto mb-1" /> : <CheckCircle2 size={20} className="mx-auto mb-1" />}
                  <span className="text-xs font-semibold">{isSkipped ? 'Restore' : 'Done'}</span>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Compact Card */}
        <div 
          className={`relative rounded-xl overflow-hidden cursor-pointer ${
            isSelected ? 'ring-2 ring-blue-500' : ''
          } ${isCompleted ? 'opacity-70' : ''} ${isSkipped ? 'opacity-60' : ''}`}
          style={{ 
            transform: `translateX(${swipeX}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            background: isCompleted 
              ? '#d1fae5' 
              : isSkipped 
                ? '#fef3c7'
                : '#ffffff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}
          onClick={handleClick}
          onTouchStart={(e) => handleStart(e.touches[0].clientX)}
          onTouchMove={(e) => handleMove(e.touches[0].clientX)}
          onTouchEnd={handleEnd}
          onMouseDown={(e) => handleStart(e.clientX)}
          onMouseMove={(e) => isDragging && handleMove(e.clientX)}
          onMouseUp={handleEnd}
          onMouseLeave={() => isDragging && handleEnd()}
        >
          {/* Left status stripe */}
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${
            isCompleted ? 'bg-emerald-500' :
            isSkipped ? 'bg-amber-500' :
            'bg-slate-300'
          }`} />
          
          {/* Main Content */}
          <div className="pl-3 pr-2 py-2">
            {/* Top Row: Number, Name, Assignee, Round, Price */}
            <div className="flex items-center gap-2">
              {/* Numbered Circle */}
              <button 
                onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
                className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                  isCompleted ? 'bg-emerald-500 text-white' :
                  isSkipped ? 'bg-amber-500 text-white' :
                  isSelected ? 'bg-blue-500 text-white' :
                  'bg-slate-700 text-white'
                }`}
              >
                {index + 1}
              </button>
              
              {/* Name & Address */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm truncate ${isCompleted || isSkipped ? 'text-slate-500' : 'text-slate-900'}`}>
                    {job.customer_name || 'Unknown'}
                  </span>
                  {job.assignee && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded font-medium">
                      {job.assignee.split(' ')[0]}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400 truncate">{streetAddress}</div>
              </div>
              
              {/* Round/Zone with dynamic color */}
              {(job.customer_zone || job.zone || job.round) && (
                <span 
                  className="text-xs px-2 py-0.5 rounded font-medium"
                  style={{ 
                    backgroundColor: `${getZoneColor(job.customer_zone || job.zone || job.round)}20`,
                    color: getZoneColor(job.customer_zone || job.zone || job.round)
                  }}
                >
                  {job.customer_zone || job.zone || job.round}
                </span>
              )}

              {/* Saved-payment badges next to round */}
              {job.customer_has_stripe && (
                <span
                  data-testid={`stripe-badge-${job.id}`}
                  title="Saved Stripe card on file"
                  className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#635BFF] text-white"
                >
                  Stripe
                </span>
              )}
              {job.customer_has_gocardless && (
                <span
                  data-testid={`gc-badge-${job.id}`}
                  title="Active GoCardless direct debit"
                  className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#1E3264] text-white"
                >
                  GC
                </span>
              )}
              
              {/* Nav Button */}
              {(job.latitude || job.customer_address) && (
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate(job); }}
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-blue-500 text-white rounded-lg"
                >
                  <Navigation size={12} />
                </button>
              )}
              
              {/* Price */}
              {!hidePrice && (
                <div className={`flex-shrink-0 font-bold text-base ${isCompleted ? 'text-emerald-600' : 'text-slate-900'}`}>
                  £{(job.price || 0).toFixed(0)}
                </div>
              )}
            </div>
            
            {/* Worker arrival/departure times — populated automatically when
                the worker enters/exits the 50m geofence around the customer.
                Mid-job state ("On site 8 min") computed live so the timer
                ticks up between location pings. */}
            {(job.worker_arrived_at || job.worker_departed_at) && (() => {
              const arrivedAt = job.worker_arrived_at ? new Date(job.worker_arrived_at) : null;
              const departedAt = job.worker_departed_at ? new Date(job.worker_departed_at) : null;
              const fmt = (d) => d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
              const totalMin = typeof job.worker_time_on_site_minutes === 'number'
                ? job.worker_time_on_site_minutes
                : (arrivedAt && departedAt ? Math.max(0, Math.round((departedAt - arrivedAt) / 60000)) : null);
              const stillOnSite = arrivedAt && !departedAt;
              const liveMin = stillOnSite ? Math.max(0, Math.round((Date.now() - arrivedAt.getTime()) / 60000)) : null;
              return (
                <div className="mt-2 text-[11px] text-slate-600" data-testid={`worker-time-${job.id}`}>
                  {arrivedAt && (
                    <span className="text-blue-600 font-medium">Arrived {fmt(arrivedAt)}</span>
                  )}
                  {departedAt && (
                    <>
                      <span className="mx-1 text-slate-300">·</span>
                      <span className="text-slate-500 font-medium">Departed {fmt(departedAt)}</span>
                    </>
                  )}
                  {totalMin !== null && (
                    <>
                      <span className="mx-1 text-slate-300">·</span>
                      <span className="text-emerald-700 font-bold">Total {totalMin} min</span>
                    </>
                  )}
                  {stillOnSite && (
                    <>
                      <span className="mx-1 text-slate-300">·</span>
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        On site {liveMin} min
                      </span>
                    </>
                  )}
                </div>
              );
            })()}
            
            {/* Action Buttons Row */}
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
              {isCompleted ? (
                <button 
                  onClick={(e) => { e.stopPropagation(); onUndoComplete && onUndoComplete(job); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-semibold"
                >
                  <RefreshCw size={14} />
                  Undo
                </button>
              ) : isSkipped ? (
                <button 
                  onClick={(e) => { e.stopPropagation(); onRestore && onRestore(job); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg text-xs font-semibold"
                >
                  <RefreshCw size={14} />
                  Restore
                </button>
              ) : (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowSkipModal(true); }}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-lg text-xs font-semibold"
                  >
                    <SkipForward size={14} />
                    Skip
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowCompleteModal(true); }}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold"
                  >
                    <Check size={14} strokeWidth={2.5} />
                    Done
                  </button>
                  {/* Paid button: admin users always see it. Workers see
                      it only if the owner enabled `can_take_payments`. */}
                  {(!isWorker || workerPermissions?.can_take_payments) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowPaymentModal(true); }}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold"
                      data-testid="job-card-paid-btn"
                    >
                      <CreditCard size={14} />
                      Paid
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Complete Job Modal */}
      {showCompleteModal && (
        <CompleteJobModal 
          job={job}
          hasPaymentMethod={hasPaymentMethod}
          paymentType={paymentType}
          onComplete={handleComplete}
          onReschedule={(date) => handleSkip('Rescheduled', date)}
          onClose={() => setShowCompleteModal(false)}
        />
      )}
      
      {/* Payment Method Modal (Done & Paid) */}
      {showPaymentModal && (
        <PaymentMethodModal
          job={job}
          onSelect={(method) => {
            setShowPaymentModal(false);
            onDoneAndPaid && onDoneAndPaid(job, { paymentMethod: method });
          }}
          onClose={() => setShowPaymentModal(false)}
        />
      )}
      
      {/* Skip Job Modal */}
      {showSkipModal && (
        <SkipJobModal 
          job={job}
          onSkip={handleSkip}
          onClose={() => setShowSkipModal(false)}
        />
      )}
    </>
  );
};

// Sortable wrapper for job cards - enables drag-and-drop reordering
// Requires holding finger down for 600ms before drag activates
const SortableJobCard = ({ job, children, isDragDisabled = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: job.id,
    disabled: isDragDisabled
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    // Allow normal touch scrolling - drag only activates after hold delay
    touchAction: 'pan-y',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="relative group"
      {...(isDragDisabled ? {} : { ...attributes, ...listeners })}
    >
      {children}
    </div>
  );
};

// ============================================
// COMPLETE JOB MODAL
// ============================================
const CompleteJobModal = ({ job, hasPaymentMethod, paymentType, onComplete, onReschedule, onClose }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  if (showDatePicker) {
    return (
      <RescheduleDatePicker 
        job={job}
        onSelect={(date) => {
          onReschedule(date);
        }}
        onBack={() => setShowDatePicker(false)}
        onClose={onClose}
        title="Reschedule Job"
      />
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex justify-center py-3">
          <div className="w-10 h-1 bg-zinc-300 rounded-full" />
        </div>
        
        <div className="px-6 pb-4">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-zinc-900 text-center">Mark as Complete?</h2>
          <p className="text-zinc-500 text-center mt-2">
            {job.customer_name || 'Unknown'} - £{(job.price || 0).toFixed(2)}
          </p>
        </div>
        
        {/* Payment info */}
        <div className="px-6 pb-4">
          {hasPaymentMethod ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <CreditCard size={20} className="text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-900">Auto-charge enabled</p>
                  <p className="text-sm text-emerald-700">
                    Payment via {paymentType === 'stripe' ? 'Stripe' : 'GoCardless'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <PoundSterling size={20} className="text-amber-600" />
                <div>
                  <p className="font-medium text-amber-900">No payment method</p>
                  <p className="text-sm text-amber-700">Invoice will be added to Owing list</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="px-6 pb-4">
          <p className="text-sm font-medium text-zinc-700 mb-3">Send notification:</p>
          <div className="flex gap-3">
            <button
              onClick={() => onComplete(true)}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors"
            >
              <Send size={18} />
              Text & Email
            </button>
            <button
              onClick={() => onComplete(false)}
              className="px-6 py-3 bg-zinc-100 text-zinc-700 rounded-xl font-medium hover:bg-zinc-200 transition-colors"
            >
              Skip
            </button>
          </div>
        </div>
        
        {/* Reschedule option */}
        <div className="px-6 pb-4">
          <button
            onClick={() => setShowDatePicker(true)}
            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-zinc-200 text-zinc-600 rounded-xl font-medium hover:bg-zinc-50 transition-colors"
          >
            <Calendar size={18} />
            Reschedule Instead
          </button>
        </div>
        
        <div className="px-6 pb-8">
          <button
            onClick={onClose}
            className="w-full py-3 text-zinc-500 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// PAYMENT METHOD MODAL (Done & Paid)
// ============================================
const PaymentMethodModal = ({ job, onSelect, onClose }) => {
  const paymentMethods = [
    { id: 'cash', name: 'Cash', icon: PoundSterling, color: 'emerald' },
    { id: 'bank_transfer', name: 'Bank Transfer', icon: CreditCard, color: 'blue' },
    { id: 'card', name: 'Card', icon: CreditCard, color: 'purple' },
    { id: 'cheque', name: 'Cheque', icon: CreditCard, color: 'orange' },
    { id: 'other', name: 'Other', icon: CreditCard, color: 'gray' },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex justify-center py-3">
          <div className="w-10 h-1 bg-zinc-300 rounded-full" />
        </div>
        
        <div className="px-6 pb-4">
          <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CreditCard size={32} className="text-indigo-600" />
          </div>
          <h2 className="text-xl font-bold text-zinc-900 text-center">How was this paid?</h2>
          <p className="text-zinc-500 text-center mt-2">
            {job.customer_name || 'Customer'} - £{(job.price || 0).toFixed(2)}
          </p>
        </div>
        
        {/* Payment method options */}
        <div className="px-6 pb-4 space-y-2">
          {paymentMethods.map(({ id, name, icon: Icon, color }) => (
            <button
              key={id}
              onClick={() => onSelect(id)}
              className={`w-full flex items-center gap-3 p-4 rounded-xl transition-colors bg-${color}-50 hover:bg-${color}-100 text-${color}-700`}
              style={{
                backgroundColor: color === 'emerald' ? '#ecfdf5' : 
                               color === 'blue' ? '#eff6ff' :
                               color === 'purple' ? '#f5f3ff' :
                               color === 'orange' ? '#fff7ed' : '#f9fafb',
              }}
            >
              <Icon size={20} />
              <span className="font-medium">{name}</span>
            </button>
          ))}
        </div>
        
        <div className="px-6 pb-8">
          <button
            onClick={onClose}
            className="w-full py-3 text-zinc-500 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SKIP JOB MODAL
// ============================================
const SkipJobModal = ({ job, onSkip, onClose }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  
  const skipReasons = [
    'Customer unavailable',
    'Weather conditions',
    'Access blocked',
    'Customer cancelled',
    'Running late',
    'Other'
  ];
  
  if (showDatePicker) {
    return (
      <RescheduleDatePicker 
        job={job}
        reason={selectedReason}
        onSelect={(date) => {
          onSkip(selectedReason || 'Rescheduled', date);
        }}
        onBack={() => setShowDatePicker(false)}
        onClose={onClose}
        title="Move to New Date"
      />
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex justify-center py-3">
          <div className="w-10 h-1 bg-zinc-300 rounded-full" />
        </div>
        
        <div className="px-6 pb-4">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <SkipForward size={32} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-zinc-900 text-center">Skip this job?</h2>
          <p className="text-zinc-500 text-center mt-2">
            {job.customer_name || 'Unknown'} - {(job.customer_address || job.address || '').split(',')[0]}
          </p>
        </div>
        
        {/* Skip reasons */}
        <div className="px-6 pb-4 space-y-2">
          <p className="text-sm font-medium text-zinc-700 mb-3">Select reason:</p>
          {skipReasons.map((reason) => (
            <button
              key={reason}
              onClick={() => onSkip(reason, null)}
              className="w-full py-3 px-4 text-left bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors flex items-center justify-between"
            >
              <span>{reason}</span>
              <ChevronRight size={16} className="text-zinc-400" />
            </button>
          ))}
        </div>
        
        {/* Reschedule option */}
        <div className="px-6 pb-4">
          <button
            onClick={() => {
              setSelectedReason('Rescheduled');
              setShowDatePicker(true);
            }}
            className="w-full flex items-center justify-center gap-2 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
          >
            <Calendar size={18} />
            Move to Another Date
          </button>
        </div>
        
        <div className="px-6 pb-8">
          <button
            onClick={onClose}
            className="w-full py-3 text-zinc-500 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// RESCHEDULE DATE PICKER
// ============================================
const RescheduleDatePicker = ({ job, reason, onSelect, onBack, onClose, title }) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };
  
  const days = getDaysInMonth(viewDate);
  
  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return d1.getDate() === d2.getDate() && 
           d1.getMonth() === d2.getMonth() && 
           d1.getFullYear() === d2.getFullYear();
  };
  
  const isToday = (date) => date && isSameDay(date, new Date());
  const isPast = (date) => date && date < new Date(new Date().setHours(0,0,0,0));
  
  const formatSelectedDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                   day === 2 || day === 22 ? 'nd' : 
                   day === 3 || day === 23 ? 'rd' : 'th';
    return `${days[date.getDay()]} ${months[date.getMonth()]} ${day}${suffix}`;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 text-center border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg text-white">
              <ArrowLeft size={20} />
            </button>
            <p className="text-white font-medium">{title}</p>
            <div className="w-9" />
          </div>
          <p className="text-white/60 text-sm">{job.customer_name || 'Customer'}</p>
          <p className="text-white text-xl font-light mt-1">{formatSelectedDate(selectedDate)}</p>
        </div>
        
        {/* Month Navigation */}
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} 
            className="p-2 hover:bg-white/10 rounded text-white"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-white font-medium">
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
          </span>
          <button 
            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} 
            className="p-2 hover:bg-white/10 rounded text-white"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        {/* Day names */}
        <div className="grid grid-cols-7 px-4">
          {dayNames.map((day, i) => (
            <div key={i} className="text-center text-white/50 text-sm py-2">{day}</div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1 px-4 pb-4">
          {days.map((date, i) => (
            <button
              key={i}
              onClick={() => date && !isPast(date) && setSelectedDate(date)}
              disabled={!date || isPast(date)}
              className={`aspect-square flex items-center justify-center rounded-full text-sm transition-colors ${
                !date ? '' :
                isPast(date) ? 'text-white/20 cursor-not-allowed' :
                isSameDay(date, selectedDate) ? 'text-white' :
                isToday(date) ? 'bg-white/20 text-white' :
                'text-white/80 hover:bg-white/10'
              }`}
              style={date && !isPast(date) && isSameDay(date, selectedDate) ? { backgroundColor: 'var(--theme-accent)' } : {}}
            >
              {date?.getDate()}
            </button>
          ))}
        </div>
        
        {/* Quick options */}
        <div className="px-4 pb-4 flex gap-2">
          {['Tomorrow', 'Next Week'].map(label => {
            const date = new Date();
            if (label === 'Tomorrow') date.setDate(date.getDate() + 1);
            if (label === 'Next Week') date.setDate(date.getDate() + 7);
            return (
              <button
                key={label}
                onClick={() => setSelectedDate(date)}
                className="flex-1 py-2 bg-white/10 text-white/80 rounded-lg text-sm hover:bg-white/20 transition-colors"
              >
                {label}
              </button>
            );
          })}
        </div>
        
        {/* Actions */}
        <div className="flex border-t border-white/10">
          <button 
            onClick={onClose}
            className="flex-1 py-4 text-white/70 hover:bg-white/10 font-medium"
          >
            Cancel
          </button>
          <button 
            onClick={() => onSelect(toLocalYMD(selectedDate))}
            className="flex-1 py-4 text-emerald-400 hover:bg-white/10 font-medium border-l border-white/10"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const DayPilot = ({ onBack, user }) => {
  // Get user role - workers can only see their own jobs
  const userRole = user?.role || 'Owner';
  const isWorker = userRole === 'Worker';
  const workerName = user?.name || '';
  
  const [selectedUser, setSelectedUser] = useState(isWorker ? workerName : 'all');
  const [selectedDate, setSelectedDate] = useState(toLocalYMD(new Date()));
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [staff, setStaff] = useState([]); // Staff will be loaded from API
  const [staffList, setStaffList] = useState([]); // Dynamic staff list from team API
  const [userInfo, setUserInfo] = useState(null);
  const [routeDirection, setRouteDirection] = useState('forward'); // 'forward' = 1 to last, 'reverse' = last to 1
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const userLocationMarkerRef = useRef(null);  // Live "you are here" pin
  const userLocationAccuracyCircleRef = useRef(null);  // Accuracy radius circle
  const geoWatchIdRef = useRef(null);  // navigator.geolocation watch handle
  const [userLocation, setUserLocation] = useState(null);  // {lat, lng, accuracy}
  const [geoError, setGeoError] = useState(null);  // permission denied / unsupported
  const [geocoding, setGeocoding] = useState(false);  // bulk geocode in-flight
  const [geocodingMsg, setGeocodingMsg] = useState('');
  // Live team locations — polled every 30s while map is open. Lets the
  // owner tap a worker chip to centre the map on them, Squeegee-style.
  const [teamLocations, setTeamLocations] = useState([]);
  const teamMarkersRef = useRef({});
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  
  // Custom job order state - allows drag-and-drop reordering
  const [customJobOrder, setCustomJobOrder] = useState({}); // { [dateKey]: [jobId1, jobId2, ...] }
  // Store original order before optimization to allow revert
  const [preOptimizeOrder, setPreOptimizeOrder] = useState(null);
  const [isOptimized, setIsOptimized] = useState(false);
  
  // DnD sensors for drag and drop - optimized for mobile touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 15, // Require 15px movement before starting drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 600, // 600ms hold before drag starts - prevents accidental drags while scrolling
        tolerance: 5, // 5px movement tolerance during delay - stricter
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Toast notification state
  const [notification, setNotification] = useState(null);
  
  // Payment failure modal state
  const [paymentFailureData, setPaymentFailureData] = useState(null);
  const [showPaymentFailure, setShowPaymentFailure] = useState(false);
  // Pin-correction prompt: shown after job completion when worker GPS is
  // 100m–1000m from saved customer pin. Always opt-in via modal.
  const [pinCorrectionSuggestion, setPinCorrectionSuggestion] = useState(null);
  
  // Invoice summary modal state
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  
  // Navigation Modal state
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState({ address: '', lat: null, lng: null });
  const [savedNavApp, setSavedNavApp] = useState('');
  const [savedNavMode, setSavedNavMode] = useState('address');
  
  // Day notes state
  const [dayNotes, setDayNotes] = useState([]);
  
  // Load day notes for selected date
  useEffect(() => {
    const loadDayNotes = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/day-notes?start_date=${selectedDate}&end_date=${selectedDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setDayNotes(data.notes || []);
        }
      } catch (err) {
        console.error('Error loading day notes:', err);
      }
    };
    
    loadDayNotes();
    // Poll every 30 seconds for real-time updates
    const interval = setInterval(loadDayNotes, 30000);
    return () => clearInterval(interval);
  }, [selectedDate]);
  
  // Show notification helper
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);
  
  // Load navigation preferences on mount
  useEffect(() => {
    const loadNavPrefs = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.navigation_app) setSavedNavApp(data.navigation_app);
          if (data.navigation_mode) setSavedNavMode(data.navigation_mode);
        }
      } catch (err) {
        console.error('Error loading navigation preferences:', err);
      }
    };
    loadNavPrefs();
  }, []);
  
  // Save navigation preferences
  const saveNavigationPreference = async (app, mode) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ navigation_app: app, navigation_mode: mode })
      });
      setSavedNavApp(app);
      setSavedNavMode(mode);
    } catch (err) {
      console.error('Error saving navigation preference:', err);
    }
  };
  
  // Open navigation modal
  const openNavigation = (address, lat = null, lng = null) => {
    setNavigationTarget({ address, lat, lng });
    setShowNavigationModal(true);
  };

  // Fetch user info for company logo
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setUserInfo(await res.json());
        }
      } catch (err) {
        console.error('Error loading user info:', err);
      }
    };
    loadUserInfo();
  }, []);

  // Worker permissions state
  const [workerPermissions, setWorkerPermissions] = useState({
    can_see_prices: true,
    can_see_customer_balance: true,
    can_send_reminders: true,
    can_create_jobs: true,
    can_mark_jobs_done: true,
    can_skip_jobs: true,
    can_see_customer_phone: true,
    can_see_customer_address: true,
    can_take_payments: false
  });
  
  // Fetch worker permissions
  useEffect(() => {
    const loadWorkerPermissions = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/worker-permissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWorkerPermissions(data);
        }
      } catch (err) {
        console.error('Error loading worker permissions:', err);
      }
    };
    if (isWorker) {
      loadWorkerPermissions();
    }
  }, [isWorker]);

  // Load Google Maps
  useEffect(() => {
    if (window.google?.maps) {
      requestAnimationFrame(() => setMapLoaded(true));
      return;
    }

    // Check if already loading
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps) {
          setMapLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    // Load Google Maps API
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Route optimization - nearest neighbor algorithm
  const optimizeRoute = useCallback((jobsToOptimize) => {
    if (!jobsToOptimize || jobsToOptimize.length <= 1) return jobsToOptimize;
    
    // Filter jobs with valid coordinates
    const jobsWithCoords = jobsToOptimize.filter(j => j.latitude && j.longitude);
    const jobsWithoutCoords = jobsToOptimize.filter(j => !j.latitude || !j.longitude);
    
    if (jobsWithCoords.length === 0) return jobsToOptimize;
    
    // Calculate distance between two points (Haversine formula)
    const getDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371; // Earth's radius in km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    };
    
    // Nearest neighbor algorithm
    const optimized = [];
    const remaining = [...jobsWithCoords];
    
    // Start from the first job (or could use a depot location)
    let current = remaining.shift();
    optimized.push(current);
    
    while (remaining.length > 0) {
      let nearestIndex = 0;
      let nearestDistance = Infinity;
      
      for (let i = 0; i < remaining.length; i++) {
        const dist = getDistance(
          current.latitude, current.longitude,
          remaining[i].latitude, remaining[i].longitude
        );
        if (dist < nearestDistance) {
          nearestDistance = dist;
          nearestIndex = i;
        }
      }
      
      current = remaining.splice(nearestIndex, 1)[0];
      optimized.push(current);
    }
    
    // Add jobs without coordinates at the end
    return [...optimized, ...jobsWithoutCoords];
  }, []);

  // Fetch jobs for the selected date
  useEffect(() => {
    const fetchJobs = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        
        // Fetch ALL jobs for the date — server applies route optimisation
        // using the user's configured starting postcode and the chosen direction.
        const direction = routeDirection === 'reverse' ? 'desc' : 'asc';
        const allJobsRes = await fetch(
          `${API_URL}/api/jobs?date=${selectedDate}&limit=200&route_optimized=true&route_direction=${direction}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (allJobsRes.ok) {
          const allJobs = await allJobsRes.json();
          // Trust the server's optimised order — don't re-shuffle client-side.
          setJobs(allJobs);
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchJobs();
  }, [selectedDate, routeDirection]);

  // Fetch team members (staff) from API
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/team/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Extract worker/planner names from team users
          const workers = (data.users || [])
            .filter(u => u.role === 'Worker' || u.role === 'Planner' || u.role === 'Owner')
            .map(u => u.name)
            .filter(Boolean);
          setStaffList(workers);
          setStaff(workers);
        }
      } catch (err) {
        console.error('Error fetching staff:', err);
      }
    };
    fetchStaff();
  }, []);
  
  // Extract unique rounds from jobs
  const roundsList = useMemo(() => {
    const rounds = new Set();
    jobs.forEach(job => {
      const round = job.customer_zone || job.zone || job.round;
      if (round) rounds.add(round);
    });
    return Array.from(rounds).sort();
  }, [jobs]);
  
  // Handle job update (for edits from JobDetailPanel)
  const handleUpdateJob = async (jobId, updates, mode = 'this') => {
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // If updating price with "all" mode, also update customer's default price
      if (updates.update_customer_price && updates.price !== undefined) {
        const job = jobs.find(j => j.id === jobId);
        if (job && job.customer_id) {
          // Update customer's price
          await fetch(`${API_URL}/api/customers/${job.customer_id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ price: updates.price })
          });
        }
        // Remove the flag before sending to job update
        delete updates.update_customer_price;
      }
      
      const res = await fetch(`${API_URL}/api/jobs/${jobId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        const updatedJob = await res.json();
        // Update local state
        setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...updates, ...updatedJob } : j));
        if (selectedJob && selectedJob.id === jobId) {
          setSelectedJob(prev => ({ ...prev, ...updates, ...updatedJob }));
        }
        showNotification('Job updated successfully', 'success');
      } else {
        showNotification('Failed to update job', 'error');
      }
    } catch (err) {
      console.error('Error updating job:', err);
      showNotification('Error updating job', 'error');
    }
  };

  // Filter jobs by selected user (match if assignee CONTAINS the name)
  const filteredJobs = useMemo(() => {
    // Workers can only see their own jobs
    if (isWorker) {
      return jobs.filter(j => j.assignee && j.assignee.toLowerCase().includes(workerName.toLowerCase()));
    }
    if (selectedUser === 'all') return jobs;
    return jobs.filter(j => j.assignee && j.assignee.toLowerCase().includes(selectedUser.toLowerCase()));
  }, [jobs, selectedUser, isWorker, workerName]);

  // Sort and apply custom order if set. Spatial route order comes from the API
  // (server applies nearest-neighbour using the user's starting postcode and
  // the chosen direction), so we just preserve that order and stack
  // scheduled → completed → skipped on top of it.
  const displayJobs = useMemo(() => {
    // Check if we have a custom order for this day (manual drag-and-drop)
    const customOrder = customJobOrder[selectedDate];
    
    if (customOrder && customOrder.length > 0) {
      const orderMap = new Map(customOrder.map((id, idx) => [id, idx]));
      return [...filteredJobs].sort((a, b) => {
        const aIdx = orderMap.has(a.id) ? orderMap.get(a.id) : 9999;
        const bIdx = orderMap.has(b.id) ? orderMap.get(b.id) : 9999;
        return aIdx - bIdx;
      });
    }
    
    // Trust API order; just bucket by status.
    const scheduled = filteredJobs.filter(j => !j.status || j.status === 'scheduled' || j.status === 'pending');
    const completed = filteredJobs.filter(j => j.status === 'done' || j.status === 'completed');
    const skipped = filteredJobs.filter(j => j.status === 'skipped');
    return [...scheduled, ...completed, ...skipped];
  }, [filteredJobs, customJobOrder, selectedDate]);
  
  // Handle drag end for job reordering
  // Save job order to backend - updates sort_order on jobs
  const saveJobOrderToBackend = useCallback(async (date, jobIds) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/jobs/order`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date, job_ids: jobIds })
      });
      if (res.ok) {
        // Refresh jobs to get updated sort_order
        fetchJobsForDateRange(selectedDate, selectedDate);
      }
    } catch (err) {
      console.error('Failed to save job order:', err);
    }
  }, [selectedDate]);

  // Pin a single job to a specific position. Other jobs route-optimise
  // around it. Persists to the customer too so future occurrences inherit it.
  const pinJobPosition = useCallback(async (jobId, position) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/jobs/${jobId}/pin-position`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ position })
      });
      if (res.ok) {
        fetchJobsForDateRange(selectedDate, selectedDate);
      }
    } catch (err) {
      console.error('Failed to pin job position:', err);
    }
  }, [selectedDate]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id && over) {
      const oldIndex = displayJobs.findIndex(j => j.id === active.id);
      const newIndex = displayJobs.findIndex(j => j.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Optimistic local reorder
        const newOrder = arrayMove(displayJobs.map(j => j.id), oldIndex, newIndex);
        setCustomJobOrder(prev => ({
          ...prev,
          [selectedDate]: newOrder
        }));
        
        // Pin ONLY the job that was dragged — server keeps everything else
        // route-optimised around the pinned position.
        pinJobPosition(active.id, newIndex);
        
        toast.success('Pinned to position', {
          description: 'Other jobs stay route-optimised around it'
        });
      }
    }
  }, [displayJobs, selectedDate, pinJobPosition]);
  
  // Clear custom order for current day
  const clearCustomOrder = useCallback(async () => {
    setCustomJobOrder(prev => {
      const newOrder = { ...prev };
      delete newOrder[selectedDate];
      return newOrder;
    });
    
    // Delete from backend
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/jobs/order/${selectedDate}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to delete job order:', err);
    }
    
    toast.success('Route reset to optimized order');
  }, [selectedDate]);
  
  // Optimize route manually with ability to revert
  const handleOptimizeRoute = useCallback(() => {
    // Save current order before optimizing
    setPreOptimizeOrder([...displayJobs]);
    
    // Run optimization
    const optimizedJobs = optimizeRoute(displayJobs);
    setJobs(optimizedJobs);
    setIsOptimized(true);
    
    // Clear any custom order for this date
    setCustomJobOrder(prev => {
      const newOrder = { ...prev };
      delete newOrder[selectedDate];
      return newOrder;
    });
    
    toast.success('Route optimized!');
  }, [displayJobs, optimizeRoute, selectedDate]);
  
  // Revert to order before optimization
  const handleRevertOptimization = useCallback(() => {
    if (preOptimizeOrder) {
      setJobs(preOptimizeOrder);
      setPreOptimizeOrder(null);
      setIsOptimized(false);
      toast.success('Reverted to previous order');
    }
  }, [preOptimizeOrder]);
  
  // Check if current day has custom order
  const hasCustomOrder = customJobOrder[selectedDate]?.length > 0;

  // Calculate stats - hide total value for workers
  const stats = useMemo(() => {
    const done = filteredJobs.filter(j => j.status === 'done' || j.status === 'completed').length;
    const skipped = filteredJobs.filter(j => j.status === 'skipped').length;
    const todo = filteredJobs.filter(j => j.status === 'scheduled' || j.status === 'pending' || !j.status).length;
    const totalValue = isWorker ? 0 : filteredJobs.reduce((sum, j) => sum + (j.price || 0), 0);
    return { done, skipped, todo, total: filteredJobs.length, totalValue };
  }, [filteredJobs, isWorker]);

  // Staff colors - dynamic based on staffList
  const getStaffColor = (name) => {
    const defaultColors = ['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16'];
    if (name === 'all') return '#6b7280';
    const index = staffList.indexOf(name);
    if (index >= 0) return defaultColors[index % defaultColors.length];
    return '#8b5cf6';
  };

  // Initialize and update map with Google Maps
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google?.maps) return;

    // Create map if not exists
    if (!mapInstanceRef.current) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: 50.95, lng: -0.13 },
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ]
      });

      mapInstanceRef.current = map;
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add markers for displayed jobs (respects route direction).
    // Skipped AND completed jobs are EXCLUDED — we don't need to navigate to
    // them, so they're dropped from the map. The numbers re-flow so the user
    // sees only what's still on the route.
    const bounds = new window.google.maps.LatLngBounds();

    const remainingJobs = displayJobs.filter((job) => {
      if (!job.latitude || !job.longitude) return false;
      const status = job.status || 'scheduled';
      return status !== 'skipped' && status !== 'done' && status !== 'completed';
    });

    remainingJobs.forEach((job, idx) => {
      const staffColor = getStaffColor(job.assignee);
      const markerColor = staffColor;

      // Create marker with custom icon
      let marker;
      
      if (userInfo?.company_logo) {
        // Create a custom marker with company logo using an HTML overlay
        const markerContent = document.createElement('div');
        markerContent.innerHTML = `
          <div style="position: relative; width: 44px; height: 44px; cursor: pointer;">
            <div style="width: 40px; height: 40px; border-radius: 50%; border: 4px solid ${markerColor}; overflow: hidden; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
              <img src="${userInfo.company_logo}" style="width: 100%; height: 100%; object-fit: cover;" />
            </div>
            <div style="position: absolute; bottom: -2px; right: -2px; width: 18px; height: 18px; border-radius: 50%; background: ${markerColor}; border: 2px solid white; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px; font-weight: bold;">${idx + 1}</div>
          </div>
        `;

        // Try to use AdvancedMarkerElement if available, otherwise fall back to regular Marker
        const AdvancedMarker = window.google.maps.marker?.AdvancedMarkerElement;
        if (AdvancedMarker) {
          marker = new AdvancedMarker({
            position: { lat: job.latitude, lng: job.longitude },
            map: mapInstanceRef.current,
            content: markerContent
          });
        }

        // Fallback if AdvancedMarkerElement not available
        if (!marker) {
          marker = new window.google.maps.Marker({
            position: { lat: job.latitude, lng: job.longitude },
            map: mapInstanceRef.current,
            label: {
              text: String(idx + 1),
              color: 'white',
              fontWeight: 'bold'
            },
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 16,
              fillColor: markerColor,
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            }
          });
        }
      } else {
        // Simple numbered marker
        marker = new window.google.maps.Marker({
          position: { lat: job.latitude, lng: job.longitude },
          map: mapInstanceRef.current,
          label: {
            text: String(idx + 1),
            color: 'white',
            fontWeight: 'bold',
            fontSize: '12px'
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 16,
            fillColor: markerColor,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          }
        });
      }

      // Add click listener for info window
      const clickHandler = () => {
        setSelectedJob(job);
        infoWindowRef.current.setContent(`
          <div style="min-width: 200px; font-family: system-ui; padding: 4px;">
            <div style="font-weight: 600; font-size: 14px;">${job.customer_name || 'Unknown'}</div>
            <div style="font-size: 12px; color: #666; margin: 4px 0;">${job.customer_address || job.address || ''}</div>
            <div style="display: flex; justify-content: space-between; padding-top: 8px; border-top: 1px solid #eee;">
              ${!isWorker ? `<span style="font-weight: 600; color: #10b981;">£${(job.price || 0).toFixed(2)}</span>` : '<span></span>'}
              <span style="font-size: 11px; padding: 2px 8px; background: ${isCompleted ? '#dcfce7' : isSkipped ? '#fef3c7' : '#dbeafe'}; color: ${isCompleted ? '#16a34a' : isSkipped ? '#d97706' : '#2563eb'}; border-radius: 9999px;">
                ${isCompleted ? 'Done' : isSkipped ? 'Skipped' : 'To Do'}
              </span>
            </div>
          </div>
        `);
        infoWindowRef.current.open(mapInstanceRef.current, marker);
      };

      if (marker.addListener) {
        marker.addListener('click', clickHandler);
      } else if (marker.content) {
        marker.content.addEventListener('click', clickHandler);
      }

      markersRef.current.push(marker);
      bounds.extend({ lat: job.latitude, lng: job.longitude });
    });

    // Fit bounds — only to remaining (not skipped/completed) jobs
    if (remainingJobs.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, 50);
    } else if (userLocation?.lat && userLocation?.lng) {
      // No remaining jobs (e.g. all done) — at least centre on the user
      // so they can still see where they are.
      mapInstanceRef.current.setCenter({ lat: userLocation.lat, lng: userLocation.lng });
      mapInstanceRef.current.setZoom(14);
    }

    // NOTE: userLocation is INTENTIONALLY omitted from the deps array below.
    // It updates on every GPS ping (a few seconds), and including it would
    // re-create every job marker on each ping — which the user sees as the
    // markers "constantly pulsing / loading". The user-location pin is rendered
    // by a separate effect that updates without touching job markers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, displayJobs, userInfo, isWorker]);


  // ────────────────────────────────────────────────────────────────────
  // LIVE USER LOCATION TRACKING
  // Adds a "you are here" pulsing blue dot to the map and keeps it
  // updated as the user moves. Permission asked on first visit; if denied
  // we silently skip (the route map still works).
  // ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!navigator?.geolocation) {
      setGeoError('Geolocation not supported by this browser');
      return undefined;
    }
    
    const handlePosition = (pos) => {
      setUserLocation({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      });
      setGeoError(null);
    };
    
    const handleError = (err) => {
      // Permission denied (code 1), position unavailable (2), timeout (3)
      if (err.code === 1) setGeoError('Location permission denied');
      else if (err.code === 2) setGeoError('Location unavailable');
      else if (err.code === 3) setGeoError('Location request timed out');
      else setGeoError('Location error');
    };
    
    // watchPosition keeps firing as the user moves; high accuracy + 10s
    // maxAge so we don't drain the battery.
    geoWatchIdRef.current = navigator.geolocation.watchPosition(
      handlePosition,
      handleError,
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    
    return () => {
      if (geoWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(geoWatchIdRef.current);
        geoWatchIdRef.current = null;
      }
    };
  }, []);
  
  // Draw / update the user pin on the map whenever location updates or the
  // map becomes ready. Renders a single solid blue circle with an animated
  // outer "sonar" ring that grows + fades on a loop.
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !window.google?.maps || !userLocation) return;
    
    const pos = { lat: userLocation.lat, lng: userLocation.lng };
    
    if (!userLocationMarkerRef.current) {
      userLocationMarkerRef.current = new window.google.maps.Marker({
        position: pos,
        map: mapInstanceRef.current,
        title: 'You are here',
        zIndex: 9999,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: '#2563eb',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3,
        },
      });
      userLocationMarkerRef.current.addListener('click', () => {
        if (!infoWindowRef.current) return;
        infoWindowRef.current.setContent(
          `<div style="font-family:system-ui;padding:4px;"><div style="font-weight:600;">You are here</div>` +
          (userLocation.accuracy ? `<div style="font-size:11px;color:#666;margin-top:2px;">Accurate to ±${Math.round(userLocation.accuracy)}m</div>` : '') +
          `</div>`
        );
        infoWindowRef.current.open(mapInstanceRef.current, userLocationMarkerRef.current);
      });
    } else {
      userLocationMarkerRef.current.setPosition(pos);
    }
    
    // Pulsing "sonar" ring around the pin — Google Maps Marker icons are
    // static, so we animate a Circle overlay's radius + opacity on a 50ms
    // setInterval. Stored in the same ref so it pans with the user.
    if (!userLocationAccuracyCircleRef.current) {
      const ring = new window.google.maps.Circle({
        map: mapInstanceRef.current,
        center: pos,
        radius: 20,
        strokeColor: '#2563eb',
        strokeOpacity: 0.6,
        strokeWeight: 2,
        fillColor: '#2563eb',
        fillOpacity: 0.2,
        clickable: false,
        zIndex: 9998,
      });
      let r = 20;
      let opacity = 0.6;
      const pulse = setInterval(() => {
        r += 4;
        opacity -= 0.03;
        if (r > 80 || opacity <= 0) {
          r = 20;
          opacity = 0.6;
        }
        if (ring.getMap()) {
          ring.setRadius(r);
          ring.setOptions({ strokeOpacity: opacity, fillOpacity: opacity * 0.3 });
        } else {
          clearInterval(pulse);
        }
      }, 60);
      userLocationAccuracyCircleRef.current = ring;
    } else {
      userLocationAccuracyCircleRef.current.setCenter(pos);
    }
  }, [mapLoaded, userLocation, userInfo]);
  
  // Render live team-worker pins as solid blue circles. The owner's own
  // pin is already handled by the userLocation effect above so we filter
  // them out here to avoid showing two pins on top of each other.
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !window.google?.maps) return;
    
    const seen = new Set();
    teamLocations.forEach((loc) => {
      if (!loc?.lat || !loc?.lng) return;
      const wid = loc.worker_id || loc.user_id || loc.worker_name;
      if (!wid) return;
      // Skip rendering the owner's own pin — useLocation effect renders it.
      if (loc.worker_id === userInfo?.id || loc.user_id === userInfo?.id) return;
      seen.add(wid);
      
      const pos = { lat: loc.lat, lng: loc.lng };
      const existing = teamMarkersRef.current[wid];
      
      if (existing) {
        if (typeof existing.setPosition === 'function') existing.setPosition(pos);
        else existing.position = pos;
      } else {
        const m = new window.google.maps.Marker({
          position: pos,
          map: mapInstanceRef.current,
          title: `${loc.worker_name || 'Worker'} (live)`,
          zIndex: 9000,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: '#2563eb',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
        });
        m.addListener('click', () => {
          if (!infoWindowRef.current) return;
          infoWindowRef.current.setContent(`
            <div style="min-width: 180px; font-family: system-ui; padding: 4px;">
              <div style="font-weight: 600; font-size: 14px;">${loc.worker_name || 'Worker'} (live)</div>
              <div style="font-size: 11px; color: #666; margin-top: 4px;">Updated ${loc.updated_at ? new Date(loc.updated_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
            </div>
          `);
          infoWindowRef.current.open(mapInstanceRef.current, m);
        });
        teamMarkersRef.current[wid] = m;
      }
    });
    
    // Sweep stale markers
    Object.keys(teamMarkersRef.current).forEach((wid) => {
      if (!seen.has(wid)) {
        const m = teamMarkersRef.current[wid];
        if (m?.setMap) m.setMap(null);
        delete teamMarkersRef.current[wid];
      }
    });
  }, [mapLoaded, teamLocations, userInfo]);
  
  // Centre map on a specific worker — bound to the Squeegee-style chip strip.
  const centreOnWorker = useCallback((workerId) => {
    const loc = teamLocations.find((l) => (l.worker_id || l.user_id || l.worker_name) === workerId);
    if (!loc || !mapInstanceRef.current) return;
    setSelectedWorkerId(workerId);
    mapInstanceRef.current.panTo({ lat: loc.lat, lng: loc.lng });
    mapInstanceRef.current.setZoom(14);
    const marker = teamMarkersRef.current[workerId];
    if (marker && infoWindowRef.current) {
      infoWindowRef.current.setContent(`
        <div style="min-width: 180px; font-family: system-ui; padding: 4px;">
          <div style="font-weight: 600; font-size: 14px;">${loc.worker_name || 'Worker'} (live)</div>
          <div style="font-size: 11px; color: #666; margin-top: 4px;">Updated ${loc.updated_at ? new Date(loc.updated_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
        </div>
      `);
      infoWindowRef.current.open(mapInstanceRef.current, marker);
    }
  }, [teamLocations]);

  // Navigate to job - opens navigation modal
  const handleNavigate = (job) => {
    const address = job.customer_address || job.address;
    // Check multiple possible field names for coordinates
    const lat = job.latitude || job.lat || job.customer_lat;
    const lng = job.longitude || job.lng || job.customer_lng;
    if (address) {
      openNavigation(address, lat, lng);
    }
  };

  // Handle job completion
  // Race navigator.geolocation against a short timeout — never block the
  // completion flow if GPS is slow/denied. The captured coords are used
  // server-side to auto-correct stale customer pins via the "Pin
  // Self-healing" feature; failure is graceful (job still completes).
  const captureWorkerGps = useCallback(() => {
    return new Promise((resolve) => {
      if (!('geolocation' in navigator)) return resolve(null);
      let done = false;
      const timer = setTimeout(() => { if (!done) { done = true; resolve(null); } }, 3000);
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          () => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            resolve(null);
          },
          { enableHighAccuracy: true, timeout: 3000, maximumAge: 30000 }
        );
      } catch (e) {
        clearTimeout(timer);
        resolve(null);
      }
    });
  }, []);

  const handleComplete = useCallback(async (job, options) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Build request body
      const requestBody = { 
        send_notification: options.sendNotification || false
      };
      
      // If marking as paid, include payment method
      if (options.markPaid) {
        requestBody.mark_paid = true;
        requestBody.payment_method = options.paymentMethod || 'cash';
      }

      // Capture worker GPS for pin auto-correction (best-effort, 3s timeout)
      const gps = await captureWorkerGps();
      if (gps) {
        requestBody.worker_lat = gps.lat;
        requestBody.worker_lng = gps.lng;
      }
      // Anchor completion to the date the card appears on, not the job's
      // stale DB scheduled_date — so early-completed jobs land on the same
      // day the user was viewing in Day Pilot.
      if (job.scheduled_date) {
        requestBody.occurrence_date = job.scheduled_date;
      }
      
      // Use the dedicated complete endpoint
      const response = await fetch(`${API_URL}/api/jobs/${job.id}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Update local state
        setJobs(prev => prev.map(j => 
          j.id === job.id ? { ...j, status: options.markPaid ? 'paid' : 'completed' } : j
        ));

        // Pin correction prompt: server tells us when worker GPS is in the
        // 100m-1000m sweet spot from the saved customer pin.
        if (data.pin_correction_suggestion) {
          setPinCorrectionSuggestion(data.pin_correction_suggestion);
        }
        
        // Check if there was a payment failure (only relevant if not marking as paid manually)
        if (data.payment_failed && !options.markPaid) {
          setPaymentFailureData({
            customerName: job.customer_name,
            customerId: job.customer_id,
            amount: data.total || job.price,
            invoiceId: data.invoice_id,
            errorMessage: data.payment_error || 'Card payment failed',
            jobId: job.id
          });
          setShowPaymentFailure(true);
        } else {
          const message = options.markPaid 
            ? `${job.customer_name || 'Job'} completed and marked as paid!`
            : `${job.customer_name || 'Job'} marked as complete!`;
          showNotification(message, 'success');
        }
      } else {
        throw new Error(data.detail || 'Failed to complete job');
      }
    } catch (err) {
      console.error('Error completing job:', err);
      showNotification('Failed to complete job', 'error');
    }
  }, [showNotification]);

  // Handle undo complete (Not Done)
  const handleUndoComplete = useCallback(async (job) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/${job.id}/undo-complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Update local state
        setJobs(prev => prev.map(j => 
          j.id === job.id ? { ...j, status: 'scheduled', completed_at: null, invoice_id: null } : j
        ));
        setSelectedJob(null);
        showNotification(`${job.customer_name || 'Job'} marked as not done, invoice cancelled`, 'success');
      } else {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to undo completion');
      }
    } catch (err) {
      console.error('Error undoing completion:', err);
      showNotification(err.message || 'Failed to undo completion', 'error');
    }
  }, [showNotification]);

  // Handle view invoice
  const handleViewInvoice = useCallback((invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  }, []);

  // Handle send invoice
  const handleSendInvoice = useCallback(async (invoice, method) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/invoices/${invoice.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ method })
      });
      
      if (response.ok) {
        showNotification(`Invoice sent via ${method}`, 'success');
      } else {
        throw new Error('Failed to send invoice');
      }
    } catch (err) {
      console.error('Error sending invoice:', err);
      showNotification('Failed to send invoice', 'error');
    }
  }, [showNotification]);

  // Handle print receipt
  const handlePrintReceipt = useCallback((job, invoice) => {
    // Open print window with receipt
    const printUrl = `${API_URL}/api/invoices/${invoice?.id || job.invoice_id}/print`;
    window.open(printUrl, '_blank');
  }, []);

  // Handle send receipt
  const handleSendReceipt = useCallback(async (job, invoice) => {
    await handleSendInvoice(invoice || { id: job.invoice_id }, 'email');
  }, [handleSendInvoice]);

  // Handle job skip (with optional reschedule)
  const handleSkip = useCallback(async (job, reason, newDate = null) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      
      if (newDate) {
        // Use the reschedule endpoint
        const response = await fetch(`${API_URL}/api/jobs/${job.id}/reschedule?new_date=${newDate}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          // Remove from current view if rescheduled to different date
          setJobs(prev => prev.filter(j => j.id !== job.id));
          showNotification(`${job.customer_name || 'Job'} moved to ${new Date(newDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`, 'success');
        } else {
          throw new Error('Failed to reschedule job');
        }
      } else {
        // Use the skip endpoint
        const skipParams = new URLSearchParams({ reason });
        if (job.scheduled_date) skipParams.set('occurrence_date', job.scheduled_date);
        const response = await fetch(`${API_URL}/api/jobs/${job.id}/skip?${skipParams.toString()}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          // Update status in local state
          setJobs(prev => prev.map(j => 
            j.id === job.id ? { ...j, status: 'skipped', skip_reason: reason } : j
          ));
          showNotification(`${job.customer_name || 'Job'} skipped: ${reason}`, 'warning');
        } else {
          throw new Error('Failed to skip job');
        }
      }
    } catch (err) {
      console.error('Error skipping/rescheduling job:', err);
      showNotification('Failed to update job', 'error');
    }
  }, [showNotification]);

  // Handle job restore (from skipped back to scheduled)
  const handleRestore = useCallback(async (job) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Use the restore endpoint (or update status to scheduled)
      const response = await fetch(`${API_URL}/api/jobs/${job.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'scheduled' })
      });
      
      if (response.ok) {
        // Update status in local state
        setJobs(prev => prev.map(j => 
          j.id === job.id ? { ...j, status: 'scheduled', skip_reason: null } : j
        ));
        showNotification(`${job.customer_name || 'Job'} restored to schedule`, 'success');
      } else {
        throw new Error('Failed to restore job');
      }
    } catch (err) {
      console.error('Error restoring job:', err);
      showNotification('Failed to restore job', 'error');
    }
  }, [showNotification]);

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(dateStr);
    selected.setHours(0, 0, 0, 0);
    
    if (selected.getTime() === today.getTime()) {
      return `Today ${date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`;
    }
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  // Map visibility state - collapsed by default on mobile for more job visibility
  const [mapCollapsed, setMapCollapsed] = useState(true);
  
  // Poll live team locations every 30s while the map is open. Lets the owner
  // tap a worker chip to centre the map on them, Squeegee-style. Declared
  // AFTER mapCollapsed so we don't hit a temporal-dead-zone error.
  useEffect(() => {
    if (mapCollapsed && !mapFullscreen) return undefined;
    let cancelled = false;
    const fetchTeam = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        if (!token) return;
        const res = await fetch(`${API_URL}/api/team/locations`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTeamLocations(Array.isArray(data?.locations) ? data.locations : []);
      } catch (_) { /* silent */ }
    };
    fetchTeam();
    const id = setInterval(fetchTeam, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [mapCollapsed, mapFullscreen]);

  // Handle map resize when expanding or going fullscreen
  useEffect(() => {
    if (mapInstanceRef.current && (!mapCollapsed || mapFullscreen)) {
      const timer = setTimeout(() => {
        window.google?.maps?.event?.trigger(mapInstanceRef.current, 'resize');
        // Re-fit bounds to remaining jobs only (not skipped or completed)
        const remainingForFit = displayJobs.filter(j =>
          j.latitude && j.longitude
          && j.status !== 'skipped'
          && j.status !== 'done'
          && j.status !== 'completed'
        );
        if (remainingForFit.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          remainingForFit.forEach(job => {
            bounds.extend({ lat: job.latitude, lng: job.longitude });
          });
          mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [mapCollapsed, mapFullscreen, displayJobs]);

  return (
    <div className="h-full flex flex-col bg-zinc-100" data-testid="day-pilot-page">
      
      {/* Compact Header Bar */}
      <div 
        className="bg-zinc-800 text-white px-3 py-2 flex items-center justify-between gap-2"
        style={{ paddingTop: 'max(env(safe-area-inset-top, 8px), 8px)' }}
      >
        {/* Back + Date */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {onBack && (
            <button onClick={onBack} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0">
              <ArrowLeft size={18} />
            </button>
          )}
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-white text-sm focus:outline-none w-28 flex-shrink-0"
          />
        </div>
        
        {/* Staff Filter Pills - compact */}
        {!isWorker && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSelectedUser('all')}
              className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                selectedUser === 'all' 
                  ? 'bg-white text-zinc-800' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              All
            </button>
            {staffList.map(name => (
              <button
                key={name}
                onClick={() => setSelectedUser(name)}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold transition-all ${
                  selectedUser === name ? 'ring-2 ring-white' : 'opacity-60'
                }`}
                style={{ backgroundColor: getStaffColor(name) }}
              >
                {name.charAt(0)}
              </button>
            ))}
          </div>
        )}
        
        {/* Stats */}
        <div className="flex items-center gap-2 text-xs flex-shrink-0">
          <span className="text-emerald-400 font-medium">{stats.done}/{stats.total}</span>
          {!isWorker && (
            <span className="text-white/70">£{stats.totalValue.toFixed(0)}</span>
          )}
        </div>
      </div>

      {/* Map Section - Collapsible or Fullscreen */}
      <div 
        className={`relative bg-zinc-200 transition-all duration-300 overflow-hidden ${
          mapFullscreen 
            ? 'fixed inset-0 z-50 h-screen' 
            : mapCollapsed 
              ? 'h-0' 
              : 'h-48 sm:h-64'
        }`}
      >
        {/* Squeegee-style worker chip strip — floats over the top of the map.
            Tap a worker to centre the map on their live position. */}
        {!mapCollapsed && teamLocations.length > 0 && (() => {
          // Compute completion ring %  for each worker from today's jobs.
          // Workers match by `assignee` (string) since that's what's set on jobs.
          const workerStats = (loc) => {
            const name = (loc.worker_name || '').toLowerCase();
            const myJobs = filteredJobs.filter((j) =>
              (j.assignee || '').toLowerCase() === name
            );
            const done = myJobs.filter((j) => j.status === 'done' || j.status === 'completed').length;
            return { total: myJobs.length, done };
          };
          
          return (
            <div className="absolute top-2 left-2 right-12 z-[1000] flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="team-chips">
              {teamLocations.map((loc) => {
                const wid = loc.worker_id || loc.user_id || loc.worker_name;
                const isSelected = selectedWorkerId === wid;
                const isMe = loc.worker_id === userInfo?.id || loc.user_id === userInfo?.id;
                const { total, done } = workerStats(loc);
                const pct = total > 0 ? done / total : 0;
                // SVG ring math — circumference of an r=14 circle
                const radius = 14;
                const circumference = 2 * Math.PI * radius;
                const ringOffset = circumference - (pct * circumference);
                return (
                  <button
                    key={wid}
                    onClick={() => centreOnWorker(wid)}
                    className={`flex-shrink-0 flex items-center gap-2 pl-1 pr-3 py-1 rounded-full shadow-md transition-all ${
                      isSelected
                        ? 'bg-blue-600 text-white ring-2 ring-blue-300'
                        : 'bg-white text-zinc-800 hover:bg-zinc-50'
                    }`}
                    data-testid={`team-chip-${wid}`}
                  >
                    {/* Avatar with progress ring */}
                    <span className="relative w-9 h-9 flex-shrink-0 flex items-center justify-center">
                      {total > 0 && (
                        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36" aria-hidden="true">
                          <circle
                            cx="18" cy="18" r={radius}
                            fill="none"
                            stroke={isSelected ? 'rgba(255,255,255,0.3)' : '#e4e4e7'}
                            strokeWidth="3"
                          />
                          <circle
                            cx="18" cy="18" r={radius}
                            fill="none"
                            stroke={pct === 1 ? '#10b981' : (pct >= 0.5 ? '#10b981' : '#f59e0b')}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={ringOffset}
                            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                          />
                        </svg>
                      )}
                      <span
                        className={`relative w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          isSelected ? 'bg-white text-blue-600' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {(loc.worker_name || '?').charAt(0).toUpperCase()}
                      </span>
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-xs font-semibold whitespace-nowrap">
                        {isMe ? 'You' : (loc.worker_name || 'Worker')}
                      </span>
                      {total > 0 && (
                        <span className={`text-[10px] ${isSelected ? 'text-blue-100' : 'text-zinc-500'} whitespace-nowrap font-medium`}>
                          {done}/{total} done
                        </span>
                      )}
                    </span>
                    <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-emerald-500'}`} />
                  </button>
                );
              })}
            </div>
          );
        })()}
        
        {/* Map container - always rendered for proper initialization */}
        <div 
          ref={mapRef} 
          className={`w-full ${mapFullscreen ? 'h-full' : 'h-48 sm:h-64'}`}
        />
        
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-200">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        )}
        
        {/* Fullscreen close button */}
        {mapFullscreen && (
          <button
            onClick={() => setMapFullscreen(false)}
            className="absolute top-4 left-4 z-[1000] bg-zinc-800 text-white p-3 rounded-xl shadow-lg hover:bg-zinc-700 transition-colors"
            style={{ top: 'max(env(safe-area-inset-top, 16px), 16px)' }}
          >
            <X size={24} />
          </button>
        )}
        
        {/* Fullscreen button (when not fullscreen and not collapsed) */}
        {!mapFullscreen && !mapCollapsed && (
          <button
            onClick={() => setMapFullscreen(true)}
            className="absolute top-2 right-2 z-[1000] bg-white p-2 rounded-lg shadow-lg hover:bg-zinc-100 transition-colors"
            title="Fullscreen map"
          >
            <Maximize2 size={18} />
          </button>
        )}
        
        {/* Fullscreen stats */}
        {mapFullscreen && (
          <div className="absolute bottom-6 left-4 right-4 z-[1000] flex items-center justify-between">
            <div className="bg-white rounded-xl shadow-lg px-4 py-2">
              <span className="font-semibold text-zinc-800">{stats.total} jobs</span>
              <span className="text-zinc-400 mx-2">•</span>
              <span className="text-emerald-600">{stats.done} done</span>
            </div>
          </div>
        )}
        
        {/* No locations message */}
        {!mapCollapsed && !isLoading && filteredJobs.filter(j => j.latitude && j.longitude).length === 0 && (
          <div className="absolute inset-0 bg-zinc-100/90 flex flex-col items-center justify-center p-4">
            <MapPin size={32} className="text-zinc-400 mb-2" />
            <p className="text-zinc-500 text-sm mb-3">No map locations</p>
            <p className="text-zinc-400 text-xs mb-3 text-center max-w-xs">
              Your customers don't have geo-coordinates yet. Run a one-off batch geocode to put their addresses on the map.
            </p>
            <button
              onClick={async () => {
                if (geocoding) return;
                setGeocoding(true);
                setGeocodingMsg('');
                try {
                  const token = localStorage.getItem('streakfree_token');
                  const res = await fetch(`${API_URL}/api/geocode/customers/batch`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setGeocodingMsg(`Geocoding ${data.pending || data.message} in the background — refresh in a minute or two`);
                  } else {
                    setGeocodingMsg(data.detail || 'Failed to start geocoding');
                  }
                } catch (e) {
                  setGeocodingMsg('Network error');
                } finally {
                  setGeocoding(false);
                }
              }}
              disabled={geocoding}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-colors"
              data-testid="bulk-geocode-btn"
            >
              {geocoding ? 'Starting…' : 'Geocode my customers'}
            </button>
            {geocodingMsg && (
              <p className={`text-xs mt-3 text-center max-w-xs ${geocodingMsg.startsWith('Failed') || geocodingMsg.startsWith('Network') ? 'text-red-600' : 'text-emerald-700'}`} data-testid="bulk-geocode-msg">
                {geocodingMsg}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Map Toggle Bar - hidden when fullscreen */}
      {!mapFullscreen && (
        <button
          onClick={() => {
            const newCollapsed = !mapCollapsed;
            setMapCollapsed(newCollapsed);
          }}
          className="flex items-center justify-center gap-2 bg-white border-b border-zinc-200 py-2 px-4 text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors"
          data-testid="map-toggle-btn"
        >
          <MapPin size={16} className="text-emerald-500" />
          {mapCollapsed ? 'Show Map' : 'Hide Map'}
          <ChevronRight size={16} className={`transition-transform ${mapCollapsed ? 'rotate-90' : '-rotate-90'}`} />
        </button>
      )}

      {/* Quick Stats Bar */}
      {!mapFullscreen && (
        <div className="flex items-center justify-between bg-white px-3 py-2 border-b border-zinc-200 text-xs">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Clock size={14} className="text-blue-500" />
              <span className="font-medium">{stats.todo} to do</span>
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle2 size={14} className="text-emerald-500" />
              <span>{stats.done} done</span>
            </span>
            {stats.skipped > 0 && (
              <span className="flex items-center gap-1">
                <SkipForward size={14} className="text-amber-500" />
                <span>{stats.skipped}</span>
              </span>
            )}
          </div>
          <button
            onClick={() => setRouteDirection(prev => prev === 'forward' ? 'reverse' : 'forward')}
            className="flex items-center gap-1.5 px-2 py-1 bg-zinc-100 hover:bg-zinc-200 rounded text-zinc-600 transition-colors"
            data-testid="route-direction-toggle"
          >
            {routeDirection === 'forward' ? (
              <ArrowDown size={12} className="text-emerald-500" />
            ) : (
              <ArrowUp size={12} className="text-blue-500" />
            )}
            <span>{routeDirection === 'forward' ? '1 → Last' : 'Last → 1'}</span>
          </button>
        </div>
      )}

      {/* Job List - Takes remaining space */}
      <div className={`flex-1 overflow-y-auto bg-slate-50 ${mapFullscreen ? 'hidden' : ''}`}>
        {/* Day Notes Banner */}
        {dayNotes.length > 0 && (
          <div 
            className="mx-3 mt-3 bg-yellow-50 border-2 border-yellow-300 rounded-xl overflow-hidden"
            data-testid="day-notes-banner"
          >
            <div className="bg-yellow-400 px-4 py-2 flex items-center gap-2">
              <StickyNote size={18} className="text-yellow-900" />
              <span className="font-bold text-yellow-900">Day Notes</span>
              <span className="text-yellow-800 text-sm">({dayNotes.length})</span>
            </div>
            <div className="p-4 space-y-2">
              {dayNotes.map((note, idx) => (
                <div key={note.id || idx} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-zinc-800 font-medium">{note.note}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {note.created_by_name || 'Team'} • {new Date(note.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {displayJobs.length === 0 ? (
          <div className="p-8 text-center text-zinc-400">
            No jobs scheduled for this date
          </div>
        ) : (
          <div className="p-3">
            {/* Custom order indicator and reset button */}
            {hasCustomOrder && (
              <div className="mb-3 flex items-center justify-between px-3 py-2 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2">
                  <GripVertical size={16} className="text-blue-500" />
                  <span className="text-sm text-blue-700 font-medium">Custom route order</span>
                </div>
                <button
                  onClick={clearCustomOrder}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  data-testid="reset-route-order-btn"
                >
                  Reset to optimized
                </button>
              </div>
            )}
            
            {/* Route Optimization Controls */}
            {!hasCustomOrder && displayJobs.length > 1 && (
              <div className="mb-3 flex items-center justify-between px-1">
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <GripVertical size={14} />
                  <span>Drag to reorder</span>
                </div>
                <div className="flex items-center gap-2">
                  {isOptimized && preOptimizeOrder && (
                    <button
                      onClick={handleRevertOptimization}
                      className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-200 transition-colors"
                      data-testid="revert-optimization-btn"
                    >
                      <RotateCcw size={12} />
                      Undo
                    </button>
                  )}
                  <button
                    onClick={handleOptimizeRoute}
                    className="flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-200 transition-colors"
                    data-testid="optimize-route-btn"
                  >
                    <Navigation size={12} />
                    Optimize Route
                  </button>
                </div>
              </div>
            )}
            
            {/* Sortable Job List */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={displayJobs.map(j => j.id)}
                strategy={verticalListSortingStrategy}
              >
                {displayJobs.map((job, idx) => (
                  <SortableJobCard key={job.id} job={job}>
                    <SwipeableJobCard
                      job={job}
                      index={idx}
                      onClick={() => setSelectedJob(job)}
                      isSelected={selectedJob?.id === job.id}
                      onComplete={handleComplete}
                      onDoneAndPaid={async (job, options = {}) => {
                        await handleComplete(job, { 
                          sendNotification: false, 
                          markPaid: true, 
                          paymentMethod: options.paymentMethod || 'cash' 
                        });
                      }}
                      onSkip={handleSkip}
                      onRestore={handleRestore}
                      onUndoComplete={handleUndoComplete}
                      getStaffColor={getStaffColor}
                      onNavigate={handleNavigate}
                      hidePrice={isWorker}
                      isWorker={isWorker}
                      workerPermissions={workerPermissions}
                    />
                  </SortableJobCard>
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
      
      {/* Toast Notification */}
      {notification && (
        <div className={`fixed bottom-24 left-4 right-4 z-50 animate-slide-up`}>
          <div className={`p-4 rounded-xl shadow-lg flex items-center gap-3 ${
            notification.type === 'success' ? 'bg-emerald-500 text-white' :
            notification.type === 'warning' ? 'bg-amber-500 text-white' :
            notification.type === 'error' ? 'bg-red-500 text-white' :
            'bg-zinc-800 text-white'
          }`}>
            {notification.type === 'success' && <CheckCircle2 size={20} />}
            {notification.type === 'warning' && <SkipForward size={20} />}
            {notification.type === 'error' && <XCircle size={20} />}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Enhanced Job Detail Panel - Slides in from right */}
      <JobDetailPanel
        job={selectedJob}
        isOpen={!!selectedJob && !showInvoiceModal}
        onClose={() => setSelectedJob(null)}
        onComplete={(job) => handleComplete(job, { sendNotification: true })}
        onDoneAndPaid={async (job, options = {}) => {
          // Complete job and mark as paid - no invoice notification since already paid
          await handleComplete(job, { 
            sendNotification: false, 
            markPaid: true, 
            paymentMethod: options.paymentMethod || 'cash' 
          });
        }}
        onReschedule={(job) => {
          // TODO: Implement reschedule modal
          showNotification('Reschedule feature coming soon', 'warning');
        }}
        onSkip={(job) => handleSkip(job, 'Skipped from job detail')}
        onNavigate={handleNavigate}
        onViewCustomer={(customerId) => {
          // Navigate to customer profile
          if (customerId) {
            window.location.href = `/customers/${customerId}`;
          }
        }}
        onSendMessage={(job) => {
          // TODO: Implement send message
          showNotification('Message feature coming soon', 'warning');
        }}
        onUndoComplete={handleUndoComplete}
        onViewInvoice={handleViewInvoice}
        onPrintReceipt={handlePrintReceipt}
        onSendReceipt={handleSendReceipt}
        onUpdateJob={handleUpdateJob}
        getStaffColor={getStaffColor}
        staffList={staffList}
        roundsList={roundsList}
        isWorker={isWorker}
        workerPermissions={workerPermissions}
      />

      {/* Invoice Summary Modal */}
      {showInvoiceModal && selectedInvoice && (
        <InvoiceSummaryModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedInvoice(null);
          }}
          onCancelInvoice={(invoice) => {
            // Refresh the job data
            setJobs(prev => prev.map(j => 
              j.invoice_id === invoice.id ? { ...j, invoice_id: null, status: 'scheduled' } : j
            ));
            showNotification('Invoice cancelled', 'success');
          }}
          onSendInvoice={handleSendInvoice}
        />
      )}

      {/* Payment Failure Modal */}
      {showPaymentFailure && paymentFailureData && (
        <PaymentFailureModal
          isOpen={showPaymentFailure}
          onClose={() => {
            setShowPaymentFailure(false);
            setPaymentFailureData(null);
          }}
          customerName={paymentFailureData.customerName}
          amount={paymentFailureData.amount}
          errorMessage={paymentFailureData.errorMessage}
          onSendInvoice={async (method) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              // Send invoice via the selected method
              const response = await fetch(`${API_URL}/api/invoices/${paymentFailureData.invoiceId}/send`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ method })
              });
              
              if (response.ok) {
                showNotification(`Invoice sent via ${method}`, 'success');
                setShowPaymentFailure(false);
                setPaymentFailureData(null);
              } else {
                showNotification('Failed to send invoice', 'error');
              }
            } catch (err) {
              console.error('Error sending invoice:', err);
              showNotification('Failed to send invoice', 'error');
            }
          }}
          onRetry={async () => {
            // Retry payment
            try {
              const token = localStorage.getItem('streakfree_token');
              const response = await fetch(`${API_URL}/api/customers/${paymentFailureData.customerId}/charge`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ 
                  amount: paymentFailureData.amount,
                  invoice_id: paymentFailureData.invoiceId
                })
              });
              
              const data = await response.json();
              
              if (response.ok && data.status === 'succeeded') {
                showNotification('Payment successful!', 'success');
                setShowPaymentFailure(false);
                setPaymentFailureData(null);
              } else {
                showNotification(data.error || 'Payment failed again', 'error');
              }
            } catch (err) {
              console.error('Error retrying payment:', err);
              showNotification('Payment retry failed', 'error');
            }
          }}
        />
      )}
      
      {/* Navigation Modal */}
      {showNavigationModal && (
        <NavigationModal
          address={navigationTarget.address}
          lat={navigationTarget.lat}
          lng={navigationTarget.lng}
          savedNavApp={savedNavApp}
          savedNavMode={savedNavMode}
          onSavePreference={saveNavigationPreference}
          onClose={() => setShowNavigationModal(false)}
        />
      )}

      {/* Pin Correction Prompt — shown after job completion when worker
          GPS is 100-1000m from the saved customer pin */}
      {pinCorrectionSuggestion && (
        <PinCorrectionPromptModal
          suggestion={pinCorrectionSuggestion}
          onClose={() => setPinCorrectionSuggestion(null)}
          onSaved={(newCoords) => {
            // Patch local job cache so next render shows the new pin
            setJobs(prev => prev.map(j =>
              j.customer_id === pinCorrectionSuggestion.customer_id
                ? { ...j, latitude: newCoords.lat, longitude: newCoords.lng }
                : j
            ));
            setPinCorrectionSuggestion(null);
          }}
        />
      )}
    </div>
  );
};

export default DayPilot;
